import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminSpecSheet, adminSpecSheetPdfUrl } from "../lib/api";

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatUpdated = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SpecSheet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminSpecSheet();
      // Strip HTML comment markers (`<!-- AUTO:X_START/END -->`). They're
      // load-bearing in the source file (the refresh script uses them to
      // find auto-managed regions) but pure noise in the rendered view.
      if (res?.markdown) {
        res.markdown = res.markdown.replace(/<!--[\s\S]*?-->/g, "").trim();
      }
      setData(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load";
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDownloadPdf = () => {
    // Open in a new tab so the existing session cookie authenticates.
    window.open(adminSpecSheetPdfUrl(), "_blank", "noopener");
    toast.success("Spec sheet PDF is downloading…");
  };

  return (
    <div className="min-h-screen bg-[#fdfaf5]" data-testid="spec-sheet-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#4a6984]/15">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to="/admin"
            data-testid="spec-sheet-back"
            className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.15em] text-[#4a6984] hover:text-[#1c9e7e] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Admin
          </Link>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="spec-sheet-refresh"
              className="h-9 border-[#4a6984]/25 text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Refresh
            </Button>
            <Button
              type="button"
              onClick={handleDownloadPdf}
              disabled={loading || !!error}
              data-testid="spec-sheet-download"
              className="h-9 bg-[#1c9e7e] hover:bg-[#177a61] text-white font-extrabold"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header card */}
        <div className="ae-card p-5 border border-[#96ded1]/45 bg-gradient-to-br from-[#96ded1]/15 to-white mb-6">
          <p className="font-extrabold uppercase text-[10px] tracking-[0.22em] text-[#1c9e7e] inline-flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Admin · Product Spec Sheet
          </p>
          <h1 className="mt-1 font-black text-2xl sm:text-3xl text-[#4a6984] tracking-tight">
            Atomic Edu — Product Spec Sheet
          </h1>
          <p className="mt-2 text-sm text-[#4a6984]/85 leading-relaxed max-w-3xl">
            Topic-organized inventory of every feature on{" "}
            <span className="font-bold">atomicedu.ca</span> — plus the AI mechanics behind each one. Source of truth is{" "}
            <code className="text-[11px] bg-[#f4f5f7] px-1.5 py-0.5 rounded">/app/memory/SPEC_SHEET.md</code>. Use it for school-board demos, sales calls, dev handoffs, or just to remember everything the product does.
          </p>
          {data ? (
            <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-[#4a6984]/70">
              <span data-testid="spec-sheet-meta-lines">
                <strong className="text-[#4a6984]">{data.lines}</strong> lines
              </span>
              <span data-testid="spec-sheet-meta-size">
                <strong className="text-[#4a6984]">{formatBytes(data.size_bytes)}</strong>
              </span>
              <span data-testid="spec-sheet-meta-updated">
                Last regenerated{" "}
                <strong className="text-[#4a6984]">{formatUpdated(data.updated_at)}</strong>
              </span>
            </div>
          ) : null}
        </div>

        {/* Body */}
        {loading ? (
          <div className="ae-card p-12 text-center text-[#4a6984]" data-testid="spec-sheet-loading">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
            <p className="font-bold">Loading spec sheet…</p>
          </div>
        ) : error ? (
          <div
            className="ae-card p-8 border border-[#b45309]/30 bg-[#fde9d2]/40 text-[#b45309]"
            data-testid="spec-sheet-error"
          >
            <p className="font-extrabold uppercase text-[10px] tracking-[0.15em]">Couldn&apos;t load</p>
            <p className="mt-2 text-sm">{error}</p>
            <p className="mt-3 text-xs text-[#b45309]/80">
              If you&apos;re not signed in as an admin, sign in first and reload. Otherwise the file may be missing on the server.
            </p>
          </div>
        ) : data ? (
          <article
            className="ae-card p-6 sm:p-10"
            data-testid="spec-sheet-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...p }) => (
                  <h1 className="text-3xl font-black text-[#1c2d3d] tracking-tight mt-0 mb-6 pb-3 border-b border-[#96ded1]/60" {...p} />
                ),
                h2: ({ node, ...p }) => (
                  <h2 className="text-2xl font-black text-[#4a6984] tracking-tight mt-10 mb-3" {...p} />
                ),
                h3: ({ node, ...p }) => (
                  <h3 className="text-lg font-black text-[#4a6984]/95 mt-6 mb-2" {...p} />
                ),
                h4: ({ node, ...p }) => (
                  <h4 className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#4a6984] mt-4 mb-1.5" {...p} />
                ),
                p: ({ node, ...p }) => (
                  <p className="text-[15px] leading-relaxed text-[#1c2d3d] mb-3" {...p} />
                ),
                ul: ({ node, ordered, ...p }) => (
                  <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[15px] leading-relaxed text-[#1c2d3d] marker:text-[#1c9e7e]" {...p} />
                ),
                ol: ({ node, ordered, ...p }) => (
                  <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[15px] leading-relaxed text-[#1c2d3d] marker:text-[#1c9e7e] marker:font-extrabold" {...p} />
                ),
                li: ({ node, ordered, ...p }) => <li className="pl-1" {...p} />,
                strong: ({ node, ...p }) => (
                  <strong className="font-extrabold text-[#1c2d3d]" {...p} />
                ),
                em: ({ node, ...p }) => <em className="italic text-[#4a6984]" {...p} />,
                a: ({ node, ...p }) => (
                  <a className="text-[#1c9e7e] font-bold hover:underline" {...p} />
                ),
                code: ({ node, inline, ...p }) =>
                  inline ? (
                    <code className="bg-[#f4f5f7] px-1.5 py-0.5 rounded text-[13px] font-mono text-[#4a6984]" {...p} />
                  ) : (
                    <code {...p} />
                  ),
                pre: ({ node, ...p }) => (
                  <pre className="bg-[#1c2d3d] text-[#96ded1] rounded-lg p-4 overflow-x-auto text-[13px] mb-4" {...p} />
                ),
                blockquote: ({ node, ...p }) => (
                  <blockquote className="border-l-4 border-[#96ded1] bg-[#96ded1]/10 px-4 py-2 my-4 text-[#1c2d3d] not-italic" {...p} />
                ),
                hr: () => <hr className="my-8 border-[#4a6984]/15" />,
                table: ({ node, ...p }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="w-full text-sm border-collapse" {...p} />
                  </div>
                ),
                thead: ({ node, ...p }) => <thead className="bg-[#f4f5f7]" {...p} />,
                th: ({ node, ...p }) => (
                  <th className="text-left font-extrabold text-[#4a6984] px-3 py-2 border-b border-[#4a6984]/20 align-top" {...p} />
                ),
                td: ({ node, ...p }) => (
                  <td className="px-3 py-2 border-b border-[#4a6984]/10 align-top text-[#1c2d3d]" {...p} />
                ),
              }}
            >
              {data.markdown}
            </ReactMarkdown>
          </article>
        ) : null}
      </div>
    </div>
  );
};

export default SpecSheet;
